<template>
  <div>
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-row>
        <v-col cols="12" md="3">
          <v-select
            v-model="fields.id_tipo_formacao"
            :items="tiposFormacao"
            :item-text="getTipoFormacaoText"
            item-value="id"
            label="Tipo de Formação"
            outlined
            dense
            :loading="loadingTipos"
            :disabled="!canEditBaseData || method === 'update'"
            :rules="[requiredRule]"
            @change="onTipoFormacaoChange"
          />
        </v-col>

        <template v-if="fields.id_tipo_formacao">
          <v-col cols="12" md="3">
            <v-text-field
              :value="statusText"
              label="Estado"
              outlined
              dense
              readonly
              disabled
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="fields.data"
              label="Data"
              type="date"
              outlined
              dense
              :disabled="!canEditBaseData"
              :rules="[requiredRule]"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="fields.obra_id"
              :items="obras"
              :item-text="getObraText"
              item-value="Id"
              label="Obra / Centro de Custo"
              outlined
              dense
              clearable
              :loading="loadingObras"
              :disabled="!canEditBaseData"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="fields.hora_inicio"
              label="Hora início"
              type="time"
              outlined
              dense
              clearable
              :disabled="!canEditBaseData"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="fields.duracao_minutos"
              label="Duração em minutos"
              type="number"
              min="0"
              outlined
              dense
              clearable
              :disabled="!canEditBaseData"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="fields.numero_acao"
              label="Nº Ação"
              outlined
              dense
              clearable
              :disabled="!canEditBaseData"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="fields.tipo_acao"
              :items="tipoAcaoOptions"
              item-text="text"
              item-value="value"
              label="Tipo de Ação"
              outlined
              dense
              clearable
              :disabled="!canEditBaseData"
              @change="onTipoAcaoChange"
            />
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="fields.documentos_entregues"
              label="Documentos entregues aos formandos"
              outlined
              dense
              auto-grow
              rows="2"
              clearable
              :disabled="!canEditBaseData"
            />
          </v-col>
        </template>
      </v-row>

      <template v-if="fields.id_tipo_formacao">
        <v-alert
          v-if="isDraft && method === 'update'"
          type="info"
          outlined
          dense
          class="mt-2"
        >
          Esta formação está em rascunho. Para permitir assinaturas, clique em
          <strong>Publicar</strong>.
        </v-alert>

        <v-alert
          v-if="isPublished && method === 'update'"
          type="success"
          outlined
          dense
          class="mt-2"
        >
          Esta formação está publicada. Os participantes e formadores já podem assinar.
        </v-alert>

        <v-alert
          v-if="isPublished && method === 'update' && !allSignaturesDone"
          type="warning"
          outlined
          dense
          class="mt-2"
        >
          Só será possível finalizar quando todos os participantes e formadores estiverem assinados.
        </v-alert>

        <v-alert
          v-if="isPublished && method === 'update' && participantes.length > 0 && !allParticipantsEvaluated"
          type="warning"
          outlined
          dense
          class="mt-2"
        >
          Todos os participantes têm de responder à avaliação antes da assinatura.
        </v-alert>

        <v-alert
          v-if="isCompleted"
          type="success"
          outlined
          dense
          class="mt-2"
        >
          Esta formação está finalizada e bloqueada para edição.
        </v-alert>

        <v-divider class="my-6" />

        <div class="eva-section-title">
          <v-icon color="primary" class="mr-2">mdi-account-tie</v-icon>
          <span>Formadores</span>
        </div>

        <div class="d-flex justify-end mb-4">
          <v-btn
            small
            color="primary"
            :disabled="!canEditFormadores"
            @click="addFormador"
          >
            <v-icon left small>mdi-plus</v-icon>
            Formador
          </v-btn>
        </div>

        <v-card
          v-for="(formador, index) in formadores"
          :key="formador.local_id"
          class="participant-card mb-4"
          outlined
        >
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" :md="method === 'update' ? 6 : 10">
                <v-autocomplete
                  v-model="formador.codigo_funcionario"
                  :items="workersInternos"
                  :item-text="getFormadorText"
                  item-value="Codigo"
                  label="Formador"
                  outlined
                  dense
                  clearable
                  :disabled="!canEditFormadores || hasFormadorSignature(formador)"
                  :loading="loadingWorkersInternos"
                  :rules="[getFormadorRule(formador)]"
                  @change="onFormadorChange(formador)"
                />
              </v-col>

              <v-col v-if="method === 'update'" cols="12" md="2">
                <v-chip
                  v-if="hasFormadorSignature(formador)"
                  small
                  color="success"
                  dark
                >
                  <v-icon left small>mdi-check</v-icon>
                  Assinado
                </v-chip>

                <v-chip v-else small color="grey" dark>
                  <v-icon left small>mdi-pencil</v-icon>
                  Por assinar
                </v-chip>
              </v-col>

              <v-col
                v-if="method === 'update'"
                cols="12"
                md="3"
                class="d-flex align-center"
              >
                <v-btn
                  small
                  color="primary"
                  :disabled="!formador.codigo_funcionario || (!canSign && !hasFormadorSignature(formador))"
                  @click="openFormadorSignatureDialog(formador, index)"
                >
                  <v-icon left small>
                    {{ hasFormadorSignature(formador) ? 'mdi-eye' : 'mdi-draw' }}
                  </v-icon>

                  {{ hasFormadorSignature(formador) ? 'Ver' : 'Assinar' }}
                </v-btn>
              </v-col>

              <v-col cols="12" md="1" class="text-center">
                <v-btn
                  icon
                  color="error"
                  :disabled="!canEditFormadores || hasFormadorSignature(formador)"
                  @click="removeFormador(index)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>

              <v-col
                v-if="method === 'update' && formador.assinatura && !formador.assinatura_path"
                cols="12"
              >
                <v-alert dense outlined type="info" class="mb-0">
                  Assinatura recolhida. Será enviada ao gravar a formação.
                </v-alert>
              </v-col>

              <v-col
                v-if="method === 'update' && formador.signed_at && !formador.assinatura_path && !formador.assinatura"
                cols="12"
              >
                <v-alert dense outlined type="success" class="mb-0">
                  Assinatura registada.
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-alert
          v-if="formadores.length === 0"
          type="warning"
          outlined
        >
          É obrigatório adicionar pelo menos um formador.
        </v-alert>

        <v-divider class="my-6" />

        <div class="eva-section-title">
          <v-icon color="primary" class="mr-2">mdi-format-list-text</v-icon>
          <span>Dados da formação</span>
        </div>

        <v-row class="mt-2">
          <v-col
            v-for="campo in campos"
            :key="campo.id"
            cols="12"
            :md="getCampoTamanho(campo)"
          >
            <v-textarea
              v-if="campo.tipo_campo === 'textarea'"
              v-model="campoValues[campo.id]"
              :label="campo.label"
              outlined
              dense
              auto-grow
              rows="3"
              :disabled="!canEditBaseData"
              :rules="campo.is_required ? [requiredRule] : []"
            />

            <v-text-field
              v-else-if="campo.tipo_campo === 'link'"
              v-model="campoValues[campo.id]"
              :label="campo.label"
              outlined
              dense
              clearable
              :disabled="!canEditBaseData"
              :rules="campo.is_required ? [requiredRule] : []"
            />

            <v-text-field
              v-else-if="campo.tipo_campo === 'date'"
              v-model="campoValues[campo.id]"
              :label="campo.label"
              type="date"
              outlined
              dense
              clearable
              :disabled="!canEditBaseData"
              :rules="campo.is_required ? [requiredRule] : []"
            />

            <v-select
              v-else-if="campo.tipo_campo === 'select' && getCampoOptions(campo).length > 0"
              v-model="campoValues[campo.id]"
              :items="getCampoOptions(campo)"
              :label="campo.label"
              outlined
              dense
              clearable
              :disabled="!canEditBaseData"
              :rules="campo.is_required ? [requiredRule] : []"
            />

            <v-radio-group
              v-else-if="campo.tipo_campo === 'boolean'"
              v-model="campoValues[campo.id]"
              :label="campo.label"
              row
              :disabled="!canEditBaseData"
              :rules="campo.is_required ? [requiredRule] : []"
            >
              <v-radio label="Sim" value="sim" />
              <v-radio label="Não" value="nao" />
            </v-radio-group>

            <v-text-field
              v-else
              v-model="campoValues[campo.id]"
              :label="campo.label"
              outlined
              dense
              clearable
              :disabled="!canEditBaseData"
              :rules="campo.is_required ? [requiredRule] : []"
            />
          </v-col>

          <v-col v-if="campos.length === 0" cols="12">
            <v-alert type="warning" outlined>
              O tipo de formação selecionado ainda não tem campos configurados.
            </v-alert>
          </v-col>
        </v-row>

        <v-divider class="my-6" />

        <div class="d-flex align-center justify-space-between mb-4">
          <div class="eva-section-title mb-0">
            <v-icon color="primary" class="mr-2">mdi-account-group</v-icon>
            <span>Participantes</span>
          </div>

          <v-btn
            small
            color="primary"
            :disabled="!canEditParticipants"
            @click="addParticipante"
          >
            <v-icon left small>mdi-plus</v-icon>
            Participante
          </v-btn>
        </div>

        <v-card
          v-for="(participante, index) in participantes"
          :key="participante.local_id"
          class="participant-card mb-4"
          outlined
        >
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" md="2">
                <v-checkbox
                  v-model="participante.is_external"
                  label="Externo"
                  dense
                  hide-details
                  :disabled="!canEditParticipants || hasSignature(participante)"
                  @change="onExternalChange(participante)"
                />
              </v-col>

              <v-col
                v-if="participante.is_external"
                cols="12"
                md="2"
              >
                <v-checkbox
                  v-model="participante.external_manual"
                  label="Não existe na BD"
                  dense
                  hide-details
                  :disabled="!canEditParticipants || hasSignature(participante)"
                  @change="onExternalManualChange(participante)"
                />
              </v-col>

              <v-col
                cols="12"
                :md="getParticipanteInputCols(participante)"
              >
                <v-autocomplete
                  v-if="!participante.is_external"
                  v-model="participante.codigo_funcionario"
                  :items="workersInternos"
                  :item-text="getInternoText"
                  item-value="Codigo"
                  label="Colaborador interno"
                  outlined
                  dense
                  clearable
                  :disabled="!canEditParticipants || hasSignature(participante)"
                  :loading="loadingWorkersInternos"
                  :rules="[getParticipanteRule(participante)]"
                  @change="onInternoChange(participante)"
                />

                <v-autocomplete
                  v-else-if="!participante.external_manual"
                  v-model="participante.codigo_funcionario"
                  :items="workersExternos"
                  :item-text="getExternoText"
                  item-value="Numero"
                  label="Colaborador externo existente"
                  outlined
                  dense
                  clearable
                  :disabled="!canEditParticipants || hasSignature(participante)"
                  :loading="loadingWorkersExternos"
                  :rules="[getParticipanteRule(participante)]"
                  @change="onExternoChange(participante)"
                />

                <v-text-field
                  v-else
                  v-model="participante.nome"
                  label="Nome do colaborador externo"
                  outlined
                  dense
                  clearable
                  :disabled="!canEditParticipants || hasSignature(participante)"
                  :rules="[getParticipanteRule(participante)]"
                />
              </v-col>

              <v-col
                v-if="method === 'update'"
                cols="12"
                md="2"
              >
                <v-chip
                  v-if="participante.compreendeu_informacoes === true"
                  small
                  color="success"
                  dark
                  class="mb-1"
                >
                  <v-icon left small>mdi-thumb-up</v-icon>
                  Compreendeu
                </v-chip>

                <v-chip
                  v-else-if="participante.compreendeu_informacoes === false"
                  small
                  color="warning"
                  dark
                  class="mb-1"
                >
                  <v-icon left small>mdi-alert</v-icon>
                  Não compreendeu
                </v-chip>

                <v-chip
                  v-else
                  small
                  color="grey"
                  dark
                  class="mb-1"
                >
                  <v-icon left small>mdi-help</v-icon>
                  Sem avaliação
                </v-chip>
              </v-col>

              <v-col
                v-if="method === 'update'"
                cols="12"
                md="2"
              >
                <v-chip
                  v-if="hasSignature(participante)"
                  small
                  color="success"
                  dark
                >
                  <v-icon left small>mdi-check</v-icon>
                  Assinado
                </v-chip>

                <v-chip
                  v-else
                  small
                  color="grey"
                  dark
                >
                  <v-icon left small>mdi-pencil</v-icon>
                  Por assinar
                </v-chip>
              </v-col>

              <v-col
                v-if="method === 'update'"
                cols="12"
                md="1"
                class="d-flex align-center"
              >
                <v-btn
                  small
                  color="primary"
                  :disabled="!canOpenParticipantSignature(participante) || (!canSign && !hasSignature(participante))"
                  @click="openParticipantSignatureDialog(participante, index)"
                >
                  <v-icon small>
                    {{ hasSignature(participante) ? 'mdi-eye' : 'mdi-draw' }}
                  </v-icon>
                </v-btn>
              </v-col>

              <v-col
                cols="12"
                md="1"
                class="text-center"
              >
                <v-btn
                  icon
                  color="error"
                  :disabled="!canEditParticipants || hasSignature(participante)"
                  @click="removeParticipante(index)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>

              <v-col
                v-if="method === 'update' && participante.assinatura && !participante.signature_url"
                cols="12"
              >
                <v-alert dense outlined type="info" class="mb-0">
                  Assinatura recolhida. Será enviada ao gravar a formação.
                </v-alert>
              </v-col>

              <v-col
                v-if="method === 'update' && participante.signed_at && !participante.signature_url && !participante.assinatura"
                cols="12"
              >
                <v-alert dense outlined type="success" class="mb-0">
                  Assinatura registada.
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-alert
          v-if="participantes.length === 0"
          type="info"
          outlined
        >
          Ainda não foram adicionados participantes.
        </v-alert>

        <v-divider class="my-6" />

        <v-row>
          <v-col
            cols="12"
            class="d-flex justify-end"
          >
            <v-btn
              text
              class="mr-2"
              @click="$router.push('/trainings')"
            >
              Voltar
            </v-btn>

            <v-btn
              v-if="canShowPublishButton"
              color="warning"
              class="mr-2"
              :loading="saving"
              :disabled="saving"
              @click="publishTraining"
            >
              <v-icon left>mdi-publish</v-icon>
              Publicar
            </v-btn>

            <v-btn
              v-if="canShowFinalizeButton"
              color="success"
              class="mr-2"
              :loading="saving"
              :disabled="saving"
              @click="finalizeTraining"
            >
              <v-icon left>mdi-check-circle</v-icon>
              Finalizar formação
            </v-btn>

            <v-btn
              color="primary"
              :loading="saving"
              :disabled="saving || isCompleted"
              @click="save"
            >
              <v-icon left>mdi-content-save</v-icon>
              Guardar
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </v-form>

    <v-dialog
      v-model="signatureDialog"
      :fullscreen="$vuetify.breakpoint.mdAndDown"
      max-width="900"
      persistent
      content-class="signature-dialog"
    >
      <v-card>
        <v-toolbar color="primary" dark>
          <v-toolbar-title>
            Assinatura de {{ selectedSignatureType === 'formador' ? 'formador' : 'participante' }}
          </v-toolbar-title>

          <v-spacer />

          <v-btn icon dark @click="closeSignatureDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-5">
          <v-row>
            <v-col cols="12">
              <div class="text-subtitle-1 font-weight-bold mb-1">
                {{ selectedSignerName }}
              </div>

              <div
                v-if="selectedSigner && !hasSelectedSignerSignature"
                class="text-caption grey--text text--darken-1 mb-4"
              >
                <span v-if="selectedSignatureType === 'participante'">
                  Primeiro indique se o formando compreendeu a formação. Depois será possível assinar.
                </span>

                <span v-else>
                  Assine no campo abaixo com tablet, telemóvel ou rato.
                </span>
              </div>
            </v-col>

            <v-col cols="12">
              <div
                v-if="selectedSigner && hasSelectedSignerSignature"
              >
                <v-alert
                  v-if="selectedSignatureType === 'participante'"
                  dense
                  outlined
                  type="info"
                >
                  Avaliação:

                  <strong>
                    {{ selectedSigner.compreendeu_informacoes === true ? 'Compreendeu' : selectedSigner.compreendeu_informacoes === false ? 'Não compreendeu' : 'Sem avaliação' }}
                  </strong>
                </v-alert>

                <v-img
                  v-if="selectedSigner.assinatura"
                  :src="selectedSigner.assinatura"
                  max-height="260"
                  contain
                >
                  <template v-slot:placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular indeterminate />
                    </v-row>
                  </template>
                </v-img>

                <v-img
                  v-else-if="selectedSignatureType === 'participante' && selectedSigner.signature_url && selectedSigner.id"
                  :src="getParticipantSignatureImageUrl(selectedSigner)"
                  max-height="260"
                  contain
                >
                  <template v-slot:placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular indeterminate />
                    </v-row>
                  </template>
                </v-img>

                <v-alert
                  v-else
                  type="info"
                  outlined
                  dense
                  class="mb-0"
                >
                  Assinatura registada.

                  <span v-if="isLocalEnvironment">
                    Em ambiente local não é enviada nem apresentada assinatura da Azure.
                  </span>
                </v-alert>
              </div>

              <div v-else-if="selectedSignatureType === 'participante'">
                <v-stepper
                  v-model="signatureStep"
                  alt-labels
                >
                  <v-stepper-header>
                    <v-stepper-step
                      :complete="signatureStep > 1"
                      step="1"
                    >
                      Avaliação
                    </v-stepper-step>

                    <v-divider />

                    <v-stepper-step step="2">
                      Assinatura
                    </v-stepper-step>
                  </v-stepper-header>

                  <v-stepper-items>
                    <v-stepper-content step="1">
                      <v-card
                        outlined
                        class="pa-4"
                      >
                        <div class="text-subtitle-1 font-weight-bold mb-3">
                          O formando compreendeu a formação?
                        </div>

                        <v-radio-group
                          v-model="participantEvaluationValue"
                          :mandatory="false"
                          column
                        >
                          <v-radio
                            label="Sim, compreendeu"
                            :value="true"
                          />

                          <v-radio
                            label="Não compreendeu"
                            :value="false"
                          />
                        </v-radio-group>

                        <v-alert
                          dense
                          outlined
                          type="info"
                          class="mb-0"
                        >
                          Esta resposta ficará gravada no participante antes da assinatura.
                        </v-alert>
                      </v-card>
                    </v-stepper-content>

                    <v-stepper-content step="2">
                      <div class="signature-pad-host">
                        <SignaturePad
                          v-if="showSignaturePad"
                          :key="signaturePadKey"
                          v-model="signatureValue"
                          ref="sig"
                        />
                      </div>
                    </v-stepper-content>
                  </v-stepper-items>
                </v-stepper>
              </div>

              <div
                v-else
                class="signature-pad-host"
              >
                <SignaturePad
                  v-if="showSignaturePad"
                  :key="signaturePadKey"
                  v-model="signatureValue"
                  ref="sig"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn
            text
            @click="closeSignatureDialog"
          >
            Cancelar
          </v-btn>

          <v-spacer />

          <v-btn
            v-if="selectedSigner && hasSelectedSignerSignature"
            color="primary"
            @click="closeSignatureDialog"
          >
            Fechar
          </v-btn>

          <template v-else-if="selectedSignatureType === 'participante'">
            <v-btn
              v-if="signatureStep === 2"
              text
              @click="signatureStep = 1"
            >
              Voltar
            </v-btn>

            <v-btn
              v-if="signatureStep === 1"
              color="primary"
              :disabled="participantEvaluationValue === null"
              @click="goToSignatureStep"
            >
              Seguinte
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>

            <v-btn
              v-else
              color="success"
              :disabled="!canSign"
              @click="saveSignature"
            >
              <v-icon left>mdi-content-save</v-icon>
              Guardar assinatura
            </v-btn>
          </template>

          <v-btn
            v-else
            color="success"
            :disabled="!canSign"
            @click="saveSignature"
          >
            <v-icon left>mdi-content-save</v-icon>
            Guardar assinatura
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Formacoes from "@/api/Formacoes.js";
import EntidadePessoalAPI from "@/api/EntidadePessoal.js";
import FuncionarioAPI from "@/api/Funcionario.js";
import Obra from "@/api/Obra.js";
import SignaturePad from "@/components/UI/SignaturePad.vue";

const DUPLICATE_STORAGE_KEY = "training_duplicate_data";

export default {
  name: "Formacao",

  components: {
    SignaturePad,
  },

  props: {
    method: {
      type: String,
      required: true,
    },

    formacao: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    valid: false,
    saving: false,

    loadingTipos: false,
    loadingObras: false,
    loadingWorkersInternos: false,
    loadingWorkersExternos: false,

    signatureDialog: false,
    selectedSignatureType: null,
    selectedSignerIndex: null,
    selectedSigner: null,

    signatureValue: null,
    showSignaturePad: false,
    signaturePadKey: 0,
    signatureStep: 1,
    participantEvaluationValue: null,

    tiposFormacao: [],
    obras: [],

    tipoAcaoOptions: [
      {
        text: "Acolhimento",
        value: "acolhimento",
      },
      {
        text: "Sensibilização",
        value: "sensibilizacao",
      },
      {
        text: "Formação local de trabalho",
        value: "formacao_local_trabalho",
      },
    ],

    fields: {
      id_tipo_formacao: null,
      status: "draft",
      obra_id: null,
      data: null,
      hora_inicio: null,
      duracao_minutos: null,
      numero_acao: null,
      documentos_entregues: null,
      tipo_acao: null,
    },

    workersExternos: [],
    workersInternos: [],

    campos: [],
    campoValues: {},

    formadores: [],
    participantes: [],

    duplicateData: null,
    isDuplicating: false,
  }),

  computed: {
    isDraft() {
      return this.fields.status === "draft";
    },

    isPublished() {
      return this.fields.status === "published";
    },

    isCompleted() {
      return this.fields.status === "completed";
    },

    isLocalEnvironment() {
      return (
        window.location.hostname === "localhost"
        || window.location.hostname === "127.0.0.1"
      );
    },

    statusText() {
      if (this.isPublished) {
        return "Publicada";
      }

      if (this.isCompleted) {
        return "Finalizada";
      }

      return "Rascunho";
    },

    canEditBaseData() {
      return !this.isCompleted
        && !this.isPublished;
    },

    canEditFormadores() {
      return !this.isCompleted
        && !this.isPublished;
    },

    canEditParticipants() {
      return !this.isCompleted;
    },

    canSign() {
      return (
        this.method === "update"
        && this.isPublished
        && !this.isCompleted
      );
    },

    allParticipantsSigned() {
      if (!this.participantes.length) {
        return false;
      }

      return this.participantes.every((participante) => {
        return this.hasSignature(participante);
      });
    },

    allParticipantsEvaluated() {
      if (!this.participantes.length) {
        return false;
      }

      return this.participantes.every((participante) => {
        return (
          participante.compreendeu_informacoes === true
          || participante.compreendeu_informacoes === false
        );
      });
    },

    allFormadoresSigned() {
      if (!this.formadores.length) {
        return false;
      }

      return this.formadores.every((formador) => {
        return this.hasFormadorSignature(formador);
      });
    },

    allSignaturesDone() {
      return this.allParticipantsSigned
        && this.allFormadoresSigned;
    },

    canShowPublishButton() {
      return this.method === "update"
        && this.isDraft;
    },

    canShowFinalizeButton() {
      return this.method === "update"
        && this.isPublished;
    },

    canFinalize() {
      return (
        this.method === "update"
        && (
          this.isPublished
          || this.fields.status === "completed"
        )
        && this.participantes.length > 0
        && this.formadores.length > 0
        && this.allParticipantsEvaluated
        && this.allSignaturesDone
      );
    },

    selectedSignerName() {
      if (!this.selectedSigner) {
        return "—";
      }

      return (
        this.selectedSigner.nome
        || this.selectedSigner.codigo_funcionario
        || "—"
      );
    },

    hasSelectedSignerSignature() {
      if (!this.selectedSigner) {
        return false;
      }

      if (
        this.selectedSignatureType
        === "formador"
      ) {
        return this.hasFormadorSignature(
          this.selectedSigner
        );
      }

      return this.hasSignature(
        this.selectedSigner
      );
    },
  },

  mounted() {
    this.fillBaseData();

    if (
      this.method === "update"
      && this.formacao
    ) {
      this.fillFromFormacao();
    }

    if (
      this.method === "create"
    ) {
      this.loadDuplicateData();
    }

    this.loadTiposFormacao();
  },

  methods: {
    fillBaseData() {
      const user =
        this.$store.state.user?.data;

      this.loadObras();
      this.loadWorkersInternos();
      this.loadWorkersExternos();

      if (
        this.method === "create"
      ) {
        this.fields.status =
          "draft";

        this.fields.data =
          new Date()
            .toISOString()
            .slice(0, 10);

        if (
          user
          && user.funcionario
        ) {
          this.formadores = [
            {
              local_id:
                this.generateLocalId(),

              id:
                null,

              codigo_funcionario:
                user.funcionario.Codigo,

              nome:
                user.funcionario.Nome
                || user.name,

              assinatura_path:
                null,

              assinatura:
                null,

              signed_at:
                null,
            },
          ];
        }
      }
    },

    loadObras() {
      this.loadingObras =
        true;

      Obra
        .all({
          tipo: "O",
        })
        .then((resp) => {
          this.obras =
            resp.data || [];
        })
        .finally(() => {
          this.loadingObras =
            false;
        });
    },

    loadWorkersInternos() {
      this.loadingWorkersInternos =
        true;

      FuncionarioAPI
        .allNoRestrictions()
        .then((res) => {
          this.workersInternos =
            res.data?.conduril
            || [];
        })
        .finally(() => {
          this.loadingWorkersInternos =
            false;
        });
    },

    loadWorkersExternos() {
      this.loadingWorkersExternos =
        true;

      EntidadePessoalAPI
        .all({
          simple: true,
        })
        .then((resp) => {
          this.workersExternos =
            resp.data || [];
        })
        .finally(() => {
          this.loadingWorkersExternos =
            false;
        });
    },

    loadTiposFormacao() {
      this.loadingTipos =
        true;

      Formacoes
        .getTiposFormacao()
        .then(({ data }) => {
          this.tiposFormacao =
            data || [];

          if (
            this.fields.id_tipo_formacao
          ) {
            this.onTipoFormacaoChange(
              this.fields.id_tipo_formacao,
              this.method === "update"
            );
          }

          if (
            this.method === "create"
            && this.duplicateData
          ) {
            this.applyDuplicateCampoValues();
          }
        })
        .finally(() => {
          this.loadingTipos =
            false;
        });
    },

    fillFromFormacao() {
      this.fields = {
        id_tipo_formacao:
          this.formacao.id_tipo_formacao,

        status:
          this.normalizeStatus(
            this.formacao.status
          ),

        obra_id:
          this.formacao.obra_id,

        data:
          this.normalizeDate(
            this.formacao.data
          ),

        hora_inicio:
          this.normalizeTime(
            this.formacao.hora_inicio
          ),

        duracao_minutos:
          this.formacao.duracao_minutos,

        numero_acao:
          this.formacao.numero_acao,

        documentos_entregues:
          this.formacao.documentos_entregues,

        tipo_acao:
          this.formacao.tipo_acao,
      };

      this.formadores =
        (
          this.formacao.formadores
          || []
        ).map((formador) => {
          return {
            local_id:
              formador.id
              || this.generateLocalId(),

            id:
              formador.id,

            codigo_funcionario:
              formador.codigo_funcionario,

            nome:
              formador.nome,

            assinatura_path:
              formador.assinatura_path,

            assinatura:
              null,

            signed_at:
              this.normalizeDateTime(
                formador.signed_at
              ),
          };
        });

      this.participantes =
        (
          this.formacao.participantes
          || []
        ).map((participante) => {
          const isExternal =
            !!participante.is_external;

          const externalManual =
            isExternal
            && !participante.codigo_funcionario;

          return {
            local_id:
              participante.id
              || this.generateLocalId(),

            id:
              participante.id,

            codigo_funcionario:
              participante.codigo_funcionario,

            is_external:
              isExternal,

            external_manual:
              externalManual,

            nome:
              participante.nome,

            added_during_signing:
              !!participante.added_during_signing,

            signature_url:
              participante.signature_url,

            assinatura:
              null,

            signed_at:
              this.normalizeDateTime(
                participante.signed_at
              ),

            compreendeu_informacoes:
              this.normalizeNullableBoolean(
                participante.compreendeu_informacoes
              ),
          };
        });
    },

    /*
     * =========================================================
     * DUPLICAÇÃO
     * =========================================================
     */

    loadDuplicateData() {
      if (
        this.$route.query.duplicate
        !== "1"
      ) {
        return;
      }

      const raw =
        sessionStorage.getItem(
          DUPLICATE_STORAGE_KEY
        );

      if (!raw) {
        console.warn(
          "[Formacao] duplicate=1 mas não existem dados no sessionStorage."
        );

        return;
      }

      try {
        const data =
          JSON.parse(raw);

        this.duplicateData =
          data;

        this.isDuplicating =
          true;

        this.fields = {
          id_tipo_formacao:
            data.id_tipo_formacao
            || null,

          status:
            "draft",

          obra_id:
            data.obra_id
            || null,

          data:
            null,

          hora_inicio:
            null,

          duracao_minutos:
            null,

          numero_acao:
            null,

          documentos_entregues:
            data.documentos_entregues
            ?? null,

          tipo_acao:
            data.tipo_acao
            ?? null,
        };

        this.formadores =
          (
            data.formadores
            || []
          ).map((formador) => {
            return {
              local_id:
                this.generateLocalId(),

              id:
                null,

              codigo_funcionario:
                formador.codigo_funcionario
                || null,

              nome:
                formador.nome
                || null,

              assinatura_path:
                null,

              assinatura:
                null,

              signed_at:
                null,
            };
          });

        this.participantes =
          (
            data.participantes
            || []
          ).map((participante) => {
            const isExternal =
              !!participante.is_external;

            const externalManual =
              participante.external_manual !== undefined
                ? !!participante.external_manual
                : (
                    isExternal
                    && !participante.codigo_funcionario
                  );

            return {
              local_id:
                this.generateLocalId(),

              id:
                null,

              codigo_funcionario:
                participante.codigo_funcionario
                || null,

              is_external:
                isExternal,

              external_manual:
                externalManual,

              nome:
                participante.nome
                || null,

              added_during_signing:
                false,

              signature_url:
                null,

              assinatura:
                null,

              signed_at:
                null,

              compreendeu_informacoes:
                null,
            };
          });
      } catch (error) {
        console.error(
          "[Formacao] Erro a ler dados da duplicação:",
          error
        );

        this.duplicateData =
          null;

        this.isDuplicating =
          false;

        this.showError(
          "Não foi possível carregar os dados da formação duplicada."
        );
      }
    },

    applyDuplicateCampoValues() {
      if (
        !this.duplicateData
      ) {
        return;
      }

      const values = {};

      (
        this.duplicateData.valores
        || []
      ).forEach((valor) => {
        values[
          valor.id_formacao_campo
        ] =
          valor.valor_texto
          ?? null;
      });

      this.campos.forEach(
        (campo) => {
          this.$set(
            this.campoValues,
            campo.id,

            Object.prototype.hasOwnProperty.call(
              values,
              campo.id
            )
              ? values[campo.id]
              : null
          );
        }
      );

      sessionStorage.removeItem(
        DUPLICATE_STORAGE_KEY
      );
    },

    /*
     * =========================================================
     * STATUS
     * =========================================================
     */

    normalizeStatus(status) {
      if (
        status === "signing"
      ) {
        return "published";
      }

      if (
        [
          "draft",
          "published",
          "completed",
        ].includes(status)
      ) {
        return status;
      }

      return "draft";
    },

    /*
     * =========================================================
     * CAMPOS DINÂMICOS / DEFAULTS
     * =========================================================
     */

    onTipoFormacaoChange(
      idTipoFormacao,
      keepValues = false
    ) {
      const tipo =
        this.tiposFormacao.find(
          (item) => {
            return item.id
              === idTipoFormacao;
          }
        );

      this.campos =
        tipo
        && Array.isArray(tipo.campos)
          ? tipo.campos
          : [];

      const currentValues =
        keepValues
          ? this.getExistingCampoValues()
          : {};

      this.campoValues =
        {};

      this.campos.forEach(
        (campo) => {
          let value =
            null;

          /*
           * UPDATE:
           * prevalece sempre o valor real da formação.
           */
          if (
            keepValues
            && Object.prototype.hasOwnProperty.call(
              currentValues,
              campo.id
            )
          ) {
            value =
              currentValues[campo.id];
          } else if (
            this.method === "create"
          ) {
            /*
             * CREATE:
             * procura o default correspondente ao
             * Tipo de Ação atual.
             */
            value =
              this.getCampoDefaultValue(
                campo
              );
          }

          this.$set(
            this.campoValues,
            campo.id,
            value
          );
        }
      );
    },

    /*
     * Um campo pode agora ter vários defaults:
     *
     * - acolhimento
     * - sensibilizacao
     * - formacao_local_trabalho
     * - null (geral)
     *
     * Regra:
     *
     * 1. procura específico para o Tipo de Ação atual;
     * 2. se não existir, procura o geral;
     * 3. se não existir nenhum, fica vazio.
     */
    getCampoDefaultValue(campo) {
      if (
        !campo
        || !Array.isArray(
          campo.defaults
        )
        || campo.defaults.length === 0
      ) {
        return null;
      }

      const tipoAcaoAtual =
        this.fields.tipo_acao
        || null;

      /*
       * =====================================================
       * 1. DEFAULT ESPECÍFICO
       * =====================================================
       */
      if (
        tipoAcaoAtual
      ) {
        const specificDefault =
          campo.defaults.find(
            (item) => {
              return (
                item
                && item.tipo_acao
                  === tipoAcaoAtual
              );
            }
          );

        if (
          specificDefault
          && specificDefault.default_value !== null
          && specificDefault.default_value !== undefined
          && String(
            specificDefault.default_value
          ).trim() !== ""
        ) {
          return specificDefault.default_value;
        }
      }

      /*
       * =====================================================
       * 2. DEFAULT GERAL
       * =====================================================
       *
       * Só é usado se não existir um específico
       * para o Tipo de Ação atual.
       */
      const generalDefault =
        campo.defaults.find(
          (item) => {
            if (!item) {
              return false;
            }

            return (
              item.tipo_acao === null
              || item.tipo_acao === undefined
              || item.tipo_acao === ""
              || item.tipo_acao === "geral"
            );
          }
        );

      if (
        generalDefault
        && generalDefault.default_value !== null
        && generalDefault.default_value !== undefined
        && String(
          generalDefault.default_value
        ).trim() !== ""
      ) {
        return generalDefault.default_value;
      }

      /*
       * =====================================================
       * 3. NÃO EXISTE DEFAULT
       * =====================================================
       */
      return null;
    },

    /*
     * Ao trocar o Tipo de Ação numa formação nova,
     * reaplicamos os defaults correspondentes.
     */
    onTipoAcaoChange() {
      if (
        this.method !== "create"
      ) {
        return;
      }

      /*
       * Numa duplicação não substituímos os valores
       * copiados da formação original.
       */
      if (
        this.isDuplicating
        && this.duplicateData
      ) {
        return;
      }

      this.campos.forEach(
        (campo) => {
          this.$set(
            this.campoValues,
            campo.id,
            this.getCampoDefaultValue(
              campo
            )
          );
        }
      );
    },

    getExistingCampoValues() {
      const values = {};

      if (
        !this.formacao
        || !this.formacao.valores
      ) {
        return values;
      }

      this.formacao.valores.forEach(
        (valor) => {
          values[
            valor.id_formacao_campo
          ] =
            valor.valor_texto;
        }
      );

      return values;
    },

    /*
     * =========================================================
     * FORMADORES
     * =========================================================
     */

    addFormador() {
      if (
        !this.canEditFormadores
      ) {
        return;
      }

      this.formadores.push({
        local_id:
          this.generateLocalId(),

        id:
          null,

        codigo_funcionario:
          null,

        nome:
          null,

        assinatura_path:
          null,

        assinatura:
          null,

        signed_at:
          null,
      });
    },

    removeFormador(index) {
      if (
        !this.canEditFormadores
      ) {
        return;
      }

      if (
        this.formadores.length
        === 1
      ) {
        this.showError(
          "A formação tem de ter pelo menos um formador."
        );

        return;
      }

      const formador =
        this.formadores[index];

      if (
        formador
        && this.hasFormadorSignature(
          formador
        )
      ) {
        this.showError(
          "Não é possível remover um formador que já assinou."
        );

        return;
      }

      this.formadores.splice(
        index,
        1
      );
    },

    onFormadorChange(formador) {
      const worker =
        this.workersInternos.find(
          (item) => {
            return String(
              item.Codigo
            ) === String(
              formador.codigo_funcionario
            );
          }
        );

      formador.nome =
        worker
          ? worker.Nome
          : null;

      formador.assinatura_path =
        null;

      formador.assinatura =
        null;

      formador.signed_at =
        null;
    },

    /*
     * =========================================================
     * PARTICIPANTES
     * =========================================================
     */

    onExternalChange(participante) {
      participante.codigo_funcionario =
        null;

      participante.nome =
        null;

      participante.external_manual =
        false;

      participante.signature_url =
        null;

      participante.assinatura =
        null;

      participante.signed_at =
        null;

      participante.compreendeu_informacoes =
        null;
    },

    onExternalManualChange(participante) {
      participante.codigo_funcionario =
        null;

      participante.nome =
        null;

      participante.signature_url =
        null;

      participante.assinatura =
        null;

      participante.signed_at =
        null;

      participante.compreendeu_informacoes =
        null;
    },

    onInternoChange(participante) {
      const worker =
        this.workersInternos.find(
          (item) => {
            return String(
              item.Codigo
            ) === String(
              participante.codigo_funcionario
            );
          }
        );

      participante.nome =
        worker
          ? worker.Nome
          : null;

      participante.external_manual =
        false;
    },

    onExternoChange(participante) {
      const codigo =
        participante.codigo_funcionario;

      const worker =
        this.workersExternos.find(
          (item) => {
            return (
              String(
                item.Numero
              ) === String(
                codigo
              )
              || String(
                item.numero
              ) === String(
                codigo
              )
            );
          }
        );

      participante.nome =
        worker
          ? (
              worker.Nome
              || worker.nome
            )
          : null;
    },

    addParticipante() {
      if (
        !this.canEditParticipants
      ) {
        return;
      }

      this.participantes.push({
        local_id:
          this.generateLocalId(),

        id:
          null,

        codigo_funcionario:
          null,

        is_external:
          false,

        external_manual:
          false,

        nome:
          null,

        added_during_signing:
          this.method === "update"
          && this.fields.status
            === "published",

        signature_url:
          null,

        assinatura:
          null,

        signed_at:
          null,

        compreendeu_informacoes:
          null,
      });
    },

    removeParticipante(index) {
      if (
        !this.canEditParticipants
      ) {
        return;
      }

      const participante =
        this.participantes[index];

      if (
        participante
        && this.hasSignature(
          participante
        )
      ) {
        this.showError(
          "Não é possível remover um participante que já assinou."
        );

        return;
      }

      this.participantes.splice(
        index,
        1
      );
    },

    /*
     * =========================================================
     * ASSINATURAS
     * =========================================================
     */

    openParticipantSignatureDialog(
      participante,
      index
    ) {
      this.openSignatureDialog(
        "participante",
        participante,
        index
      );
    },

    openFormadorSignatureDialog(
      formador,
      index
    ) {
      this.openSignatureDialog(
        "formador",
        formador,
        index
      );
    },

    openSignatureDialog(
      type,
      signer,
      index
    ) {
      if (
        !this.canSign
        && !this.getSignerHasSignature(
          type,
          signer
        )
      ) {
        this.showError(
          "Só é possível assinar formações publicadas."
        );

        return;
      }

      this.selectedSignatureType =
        type;

      this.selectedSigner =
        signer;

      this.selectedSignerIndex =
        index;

      this.signatureValue =
        null;

      this.showSignaturePad =
        false;

      this.signaturePadKey +=
        1;

      this.signatureStep =
        1;

      this.participantEvaluationValue =
        type === "participante"
          ? this.normalizeNullableBoolean(
              signer.compreendeu_informacoes
            )
          : null;

      this.signatureDialog =
        true;

      if (
        this.getSignerHasSignature(
          type,
          signer
        )
      ) {
        return;
      }

      if (
        type === "formador"
      ) {
        this.prepareSignaturePad();
      }
    },

    goToSignatureStep() {
      if (
        this.participantEvaluationValue
          !== true
        && this.participantEvaluationValue
          !== false
      ) {
        this.showError(
          "Tem de indicar se o formando compreendeu ou não a formação."
        );

        return;
      }

      if (
        this.selectedSignatureType
          === "participante"
        && this.selectedSignerIndex
          !== null
      ) {
        const participante =
          this.participantes[
            this.selectedSignerIndex
          ];

        this.$set(
          participante,
          "compreendeu_informacoes",
          this.participantEvaluationValue
        );

        this.selectedSigner =
          participante;
      }

      this.signatureStep =
        2;

      this.prepareSignaturePad();
    },

    prepareSignaturePad() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.showSignaturePad =
            true;

          this.$nextTick(() => {
            setTimeout(() => {
              const sig =
                Array.isArray(
                  this.$refs.sig
                )
                  ? this.$refs.sig[0]
                  : this.$refs.sig;

              if (
                sig
                && sig.resizeCanvas
              ) {
                sig.resizeCanvas();
              }
            }, 150);
          });
        }, 300);
      });
    },

    closeSignatureDialog() {
      this.signatureDialog =
        false;

      this.selectedSignatureType =
        null;

      this.selectedSigner =
        null;

      this.selectedSignerIndex =
        null;

      this.signatureValue =
        null;

      this.showSignaturePad =
        false;

      this.signatureStep =
        1;

      this.participantEvaluationValue =
        null;
    },

    saveSignature() {
      if (!this.canSign) {
        this.showError(
          "Só é possível assinar formações publicadas."
        );

        return;
      }

      if (
        this.selectedSignerIndex
          === null
        || !this.selectedSignatureType
      ) {
        return;
      }

      if (
        this.selectedSignatureType
          === "participante"
        && this.participantEvaluationValue
          !== true
        && this.participantEvaluationValue
          !== false
      ) {
        this.showError(
          "Tem de indicar se o formando compreendeu ou não a formação."
        );

        return;
      }

      const sigRef =
        Array.isArray(
          this.$refs.sig
        )
          ? this.$refs.sig[0]
          : this.$refs.sig;

      const sig =
        sigRef
        && sigRef.getDataUrl
          ? sigRef.getDataUrl()
          : this.signatureValue;

      if (!sig) {
        this.showError(
          "A assinatura é obrigatória."
        );

        return;
      }

      if (
        this.selectedSignatureType
          === "formador"
      ) {
        const formador =
          this.formadores[
            this.selectedSignerIndex
          ];

        this.$set(
          formador,
          "assinatura",
          sig
        );

        this.$set(
          formador,
          "signed_at",
          this.getCurrentDateTimeLocal()
        );
      } else {
        const participante =
          this.participantes[
            this.selectedSignerIndex
          ];

        this.$set(
          participante,
          "compreendeu_informacoes",
          this.participantEvaluationValue
        );

        this.$set(
          participante,
          "assinatura",
          sig
        );

        this.$set(
          participante,
          "signed_at",
          this.getCurrentDateTimeLocal()
        );
      }

      this.closeSignatureDialog();
    },

    getSignerHasSignature(
      type,
      signer
    ) {
      if (
        type === "formador"
      ) {
        return this.hasFormadorSignature(
          signer
        );
      }

      return this.hasSignature(
        signer
      );
    },

    hasSignature(participante) {
      return (
        !!participante.signature_url
        || !!participante.assinatura
        || !!participante.signed_at
      );
    },

    hasFormadorSignature(formador) {
      return (
        !!formador.assinatura_path
        || !!formador.assinatura
        || !!formador.signed_at
      );
    },

    canOpenParticipantSignature(
      participante
    ) {
      if (!participante) {
        return false;
      }

      if (
        participante.is_external
        && participante.external_manual
      ) {
        return !!participante.nome;
      }

      return !!participante.codigo_funcionario;
    },

    getParticipantSignatureImageUrl(
      participante
    ) {
      if (
        !participante
        || !participante.id
      ) {
        return null;
      }

      return Formacoes
        .assinaturaParticipanteUrl(
          participante.id
        );
    },

    /*
     * =========================================================
     * PUBLICAR / FINALIZAR
     * =========================================================
     */

    publishTraining() {
      if (
        this.method !== "update"
        || !this.isDraft
      ) {
        return;
      }

      if (
        !this.$refs.form.validate()
      ) {
        return;
      }

      if (
        this.formadores.length
        === 0
      ) {
        this.showError(
          "É obrigatório adicionar pelo menos um formador."
        );

        return;
      }

      this.fields.status =
        "published";

      this.save();
    },

    finalizeTraining() {
      if (
        this.method !== "update"
        || !this.isPublished
      ) {
        return;
      }

      if (
        !this.$refs.form.validate()
      ) {
        return;
      }

      if (
        this.participantes.length
        === 0
      ) {
        this.showError(
          "É obrigatório adicionar pelo menos um participante."
        );

        return;
      }

      if (
        this.formadores.length
        === 0
      ) {
        this.showError(
          "É obrigatório adicionar pelo menos um formador."
        );

        return;
      }

      if (
        !this.allParticipantsEvaluated
      ) {
        this.showError(
          "Todos os participantes têm de responder à avaliação antes de finalizar."
        );

        return;
      }

      if (
        !this.allSignaturesDone
      ) {
        this.showError(
          "Só é possível finalizar quando todos os participantes e formadores estiverem assinados."
        );

        return;
      }

      this.fields.status =
        "completed";

      this.save(true);
    },

    /*
     * =========================================================
     * SAVE
     * =========================================================
     */

    save(
      isFinalizing = false
    ) {
      if (
        !this.$refs.form.validate()
      ) {
        return;
      }

      if (
        this.formadores.length
        === 0
      ) {
        this.showError(
          "É obrigatório adicionar pelo menos um formador."
        );

        return;
      }

      if (
        this.method === "create"
      ) {
        this.fields.status =
          "draft";
      }

      if (
        this.formacao
        && this.formacao.status
          === "completed"
      ) {
        this.showError(
          "Não é possível alterar uma formação finalizada."
        );

        return;
      }

      if (
        this.formacao
        && this.formacao.status
          === "published"
        && this.fields.status
          === "draft"
      ) {
        this.showError(
          "Não é possível voltar uma formação publicada para rascunho."
        );

        return;
      }

      if (
        this.fields.status
          === "completed"
        && !isFinalizing
        && !this.canFinalize
      ) {
        this.showError(
          "Só é possível finalizar a formação quando todos os participantes responderem à avaliação e todos os participantes e formadores estiverem assinados."
        );

        return;
      }

      this.saving =
        true;

      const payload =
        this.buildPayload();

      const request =
        this.method === "create"
          ? Formacoes.create(
              payload
            )
          : Formacoes.update(
              this.formacao.id,
              payload
            );

      request
        .then(({ data }) => {
          sessionStorage.removeItem(
            DUPLICATE_STORAGE_KEY
          );

          if (
            this.method !== "create"
          ) {
            window.location.reload();
          } else {
            this.goToTraining(
              data.id
            );
          }
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message
            || error?.response?.data?.error
            || "Ocorreu um erro ao guardar a formação.";

          this.showError(
            message
          );
        })
        .finally(() => {
          this.saving =
            false;
        });
    },

    buildPayload() {
      return {
        id_tipo_formacao:
          this.fields.id_tipo_formacao,

        status:
          this.method === "create"
            ? "draft"
            : this.fields.status,

        obra_id:
          this.fields.obra_id,

        data:
          this.fields.data,

        hora_inicio:
          this.fields.hora_inicio,

        duracao_minutos:
          this.fields.duracao_minutos,

        numero_acao:
          this.fields.numero_acao,

        documentos_entregues:
          this.fields.documentos_entregues,

        tipo_acao:
          this.fields.tipo_acao,

        formadores:
          this.formadores.map(
            (formador) => {
              const item = {
                id:
                  formador.id,

                codigo_funcionario:
                  formador.codigo_funcionario,

                nome:
                  formador.nome,
              };

              if (
                this.method
                === "update"
              ) {
                item.assinatura =
                  formador.assinatura
                  || null;

                item.assinatura_path =
                  formador.assinatura_path
                  || null;

                item.signed_at =
                  formador.signed_at
                  || null;
              }

              return item;
            }
          ),

        valores:
          this.campos.map(
            (campo) => {
              return {
                id_formacao_campo:
                  campo.id,

                valor_texto:
                  this.campoValues[
                    campo.id
                  ] || null,

                valor_json:
                  null,
              };
            }
          ),

        participantes:
          this.participantes.map(
            (participante) => {
              const externalManual =
                !!participante.is_external
                && !!participante.external_manual;

              const item = {
                id:
                  participante.id,

                codigo_funcionario:
                  externalManual
                    ? null
                    : participante.codigo_funcionario,

                is_external:
                  !!participante.is_external,

                external_manual:
                  externalManual,

                nome:
                  participante.nome,

                added_during_signing:
                  participante.added_during_signing,

                compreendeu_informacoes:
                  participante.compreendeu_informacoes,
              };

              if (
                this.method
                === "update"
              ) {
                item.assinatura =
                  participante.assinatura
                  || null;

                item.signature_url =
                  participante.signature_url
                  || null;

                item.signed_at =
                  participante.signed_at
                  || null;
              }

              return item;
            }
          ),
      };
    },

    goToTraining(id) {
      const path =
        "/trainings/"
        + id;

      if (
        this.$route.path
        === path
      ) {
        return;
      }

      this.$router
        .push(path)
        .catch((error) => {
          if (
            error
            && error.name
              !== "NavigationDuplicated"
          ) {
            throw error;
          }
        });
    },

    getParticipanteInputCols(
      participante
    ) {
      if (
        this.method === "update"
      ) {
        if (
          participante.is_external
        ) {
          return 3;
        }

        return 4;
      }

      if (
        participante.is_external
      ) {
        return 7;
      }

      return 9;
    },

    getCampoTamanho(campo) {
      const tamanho =
        Number(
          campo?.tamanho
        );

      if (
        !tamanho
        || tamanho < 1
        || tamanho > 12
      ) {
        return 12;
      }

      return tamanho;
    },

    getCampoOptions(campo) {
      if (!campo) {
        return [];
      }

      if (
        Array.isArray(
          campo.options
        )
      ) {
        return campo.options;
      }

      if (
        Array.isArray(
          campo.opcoes
        )
      ) {
        return campo.opcoes;
      }

      if (
        campo.valor_json
        && Array.isArray(
          campo.valor_json.options
        )
      ) {
        return campo.valor_json.options;
      }

      if (
        campo.valor_json
        && Array.isArray(
          campo.valor_json.opcoes
        )
      ) {
        return campo.valor_json.opcoes;
      }

      return [];
    },

    requiredRule(value) {
      return !!value
        || "Campo obrigatório.";
    },

    getParticipanteRule(
      participante
    ) {
      return () => {
        if (
          participante.is_external
          && participante.external_manual
        ) {
          if (
            !participante.nome
          ) {
            return "Indique o nome do colaborador externo.";
          }

          return true;
        }

        if (
          !participante.codigo_funcionario
        ) {
          return participante.is_external
            ? "Selecione o colaborador externo existente."
            : "Selecione o colaborador interno.";
        }

        return true;
      };
    },

    getFormadorRule(formador) {
      return () => {
        if (
          !formador.codigo_funcionario
        ) {
          return "Selecione o formador.";
        }

        return true;
      };
    },

    getTipoFormacaoText(item) {
      if (!item) {
        return "";
      }

      if (
        item.codigo
        && item.nome
      ) {
        return item.codigo
          + " - "
          + item.nome;
      }

      return item.nome
        || item.codigo
        || "";
    },

    getObraText(item) {
      if (!item) {
        return "";
      }

      const codigo =
        item.Codigo
        || item.codigo
        || "";

      const descricao =
        item.Descricao
        || item.descricao
        || item.Nome
        || item.nome
        || "";

      return (
        codigo
        && descricao
          ? codigo
            + " - "
            + descricao
          : descricao
            || codigo
      );
    },

    getFormadorText(item) {
      if (!item) {
        return "";
      }

      const codigo =
        item.Codigo || "";

      const nome =
        item.Nome || "";

      return (
        codigo
        && nome
          ? codigo
            + " - "
            + nome
          : nome
            || codigo
      );
    },

    getInternoText(item) {
      if (!item) {
        return "";
      }

      const codigo =
        item.Codigo || "";

      const nome =
        item.Nome || "";

      return (
        codigo
        && nome
          ? codigo
            + " - "
            + nome
          : nome
            || codigo
      );
    },

    getExternoText(item) {
      if (!item) {
        return "";
      }

      const numero =
        item.Numero
        || item.numero
        || "";

      const nome =
        item.Nome
        || item.nome
        || "";

      return (
        numero
        && nome
          ? numero
            + " - "
            + nome
          : nome
            || numero
      );
    },

    normalizeDate(value) {
      if (!value) {
        return null;
      }

      return String(
        value
      ).slice(
        0,
        10
      );
    },

    normalizeTime(value) {
      if (!value) {
        return null;
      }

      return String(
        value
      ).slice(
        0,
        5
      );
    },

    normalizeDateTime(value) {
      if (!value) {
        return null;
      }

      return String(
        value
      ).slice(
        0,
        16
      );
    },

    normalizeNullableBoolean(value) {
      if (
        value === true
        || value === 1
        || value === "1"
        || value === "true"
      ) {
        return true;
      }

      if (
        value === false
        || value === 0
        || value === "0"
        || value === "false"
      ) {
        return false;
      }

      return null;
    },

    getCurrentDateTimeLocal() {
      const now =
        new Date();

      const offset =
        now.getTimezoneOffset();

      const local =
        new Date(
          now.getTime()
          - offset * 60000
        );

      return local
        .toISOString()
        .slice(
          0,
          16
        );
    },

    generateLocalId() {
      return (
        "local-"
        + Math.random()
          .toString(36)
          .substring(2)
        + Date.now()
      );
    },

    showError(message) {
      alert(message);
    },
  },
};
</script>

<style scoped>
.eva-section-title {
  display: flex;
  align-items: center;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 0.2px;
  margin-bottom: 8px;
}

.participant-card {
  border-radius: 14px;
}

.signature-dialog :deep(.v-dialog) {
  border-radius: 16px;
}

.signature-pad-host {
  width: 100%;
  min-height: 300px;
  position: relative;
  pointer-events: auto;
}

.signature-pad-host :deep(canvas) {
  pointer-events: auto !important;
  touch-action: none !important;
  cursor: crosshair !important;
}

.signature-pad-host :deep(*) {
  pointer-events: auto;
}
</style>